/* @flow */

import clsx from 'clsx';
import { useCallback } from 'react';
import * as React from 'react';

import Localizer from '../../helpers/localization';
import './Picto.css';

export type PictoPropType = {
  +'aria-controls'?: boolean,
  +'aria-expanded'?: boolean,
  +'aria-haspopup'?: boolean,
  +'aria-hidden'?: boolean,
  +'aria-label'?: string,
  +children: React.Node,
  +className?: string,
  +dataCustom?: string,
  +defaultAriaLabelKey: string,
  +draggable?: boolean,
  +forceHoverEffect?: boolean,
  +hasBackground?: boolean,
  +id?: string,
  +isDisabled?: boolean,
  +key?: string,
  +onClick?: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement> | SyntheticKeyboardEvent<HTMLElement>) => void,
  +onMouseDown?: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void,
  +onMouseEnter?: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void,
  +onMouseLeave?: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void,
  +onMouseMove?: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void,
  +onMouseUp?: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void,
};

const Picto = ({ children, ...delegated }: PictoPropType, ref: React.ElementRef<'button'> | React.ElementRef<'div'> | null): React.Node => {
  const {
    'aria-controls': ariaControls,
    'aria-expanded': ariaExpanded,
    'aria-haspopup': ariaHaspopup,
    'aria-hidden': ariaHidden,
    'aria-label': ariaLabel,
    className = '',
    dataCustom,
    defaultAriaLabelKey,
    draggable,
    forceHoverEffect,
    hasBackground,
    id,
    isDisabled,
    key,
    onClick,
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
    onMouseMove,
    onMouseUp,
  } = delegated;

  const handleOnKeyDown = useCallback(
    (event: SyntheticKeyboardEvent<HTMLElement>) => {
      const { key } = event;

      if (typeof onClick === 'function' && (key === 'Enter' || key === ' ')) {
        event.preventDefault();
        onClick(event);
      }
    },
    [onClick],
  );

  const content = (
    <>
      {hasBackground ? <div className='background' /> : null}
      <svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'>
        {children}
      </svg>
    </>
  );

  if (onClick !== undefined) {
    // Clickable
    return (
      <div
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHaspopup}
        aria-label={ariaLabel ?? Localizer.localize(defaultAriaLabelKey)}
        className={clsx('pictoElement', 'hoverEffect', className)}
        data-custom={dataCustom}
        disabled={isDisabled}
        id={id}
        key={key}
        onClick={onClick}
        onKeyDown={handleOnKeyDown}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        ref={ref}
        role='button'
        tabIndex={0}
      >
        {content}
      </div>
    );
  }

  const ariaHiddenValue = ariaHidden ?? false;

  // Not clickable
  return (
    <div
      aria-hidden={ariaHiddenValue}
      aria-label={ariaHiddenValue ? undefined : (ariaLabel ?? Localizer.localize(defaultAriaLabelKey))}
      className={clsx('pictoElement', className, forceHoverEffect && 'hoverEffect')}
      data-custom={dataCustom}
      data-disabled={isDisabled}
      draggable={draggable}
      id={id}
      key={key}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      ref={ref}
      role='img'
    >
      {content}
    </div>
  );
};

export default React.forwardRef<PictoPropType, HTMLElement>(Picto) as React.ComponentType<{
  ...PictoPropType,
  +ref?: React.Ref<HTMLElement>,
}>;
